'use memo'

import { useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { App } from 'antd'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { readSessionUpdateError } from '@sportninja/common/features/registration/sessionUpdateError'
import { teamIdentity } from '@sportninja/common/features/registration/startStep/teamDisplay'
import type { StartRole } from '@sportninja/common/features/registration/startStep/types'
import type { Division } from '@sportninja/common/types'
import { RegistrationContext } from '../../../context/RegistrationContext'
import {
  withSelectionParams,
  withoutSelectionParams,
  linkParamPresence,
} from '../../../utils/selectionParams'
import { REGISTRATION_STEPS } from '../../Main'
import { checkIfUserIsVerified } from '../../New_ConfirmYourEmail'
import type { CommittedSelection } from '../utils/committedSelection'

export interface CommitOptions {
  /**
   * The role's registration type id. Without one only the division is
   * committed — the fallback Next has always had for a reader who has not
   * answered the role.
   */
  registrationTypeId: string | undefined
  /** Written into an existing `?type=` param so it stays honest. */
  roleKey: StartRole | null
  /**
   * Drop every link param once the reader is through to register-entity.
   *
   * Set only by the `?team_id=` auto-advance. A link that has done its job
   * would otherwise send Back — in-app or browser — to a start step that
   * advances straight forward again. Only on the verified branch: the sign-up
   * and email legs need the link still there when the reader returns.
   */
  consumeLink: boolean
}

/**
 * The start step's commit: persist the answer, hand it to the context the
 * register step renders from, and move on.
 *
 * The ONLY forward path into register-entity. Next and the `?team_id=`
 * auto-advance both call this, so the context receives the start step's team
 * and division in exactly one place — see `committedSelection`.
 */
export const useStartCommit = () => {
  const {
    isLogged,
    userId,
    updateSessionStart,
    updateSessionDivision,
    setStep,
    deleteSession,
    // Named apart from the start step's own LOCAL selection: the context copy
    // is what the register step renders, and is written only here.
    setSelectedTeam: setContextSelectedTeam,
    setDivision,
  } = useContext(RegistrationContext)
  const { message } = App.useApp()
  const history = useHistory()
  const [isSubmitting, setIsSubmitting] = useState(false)

  /** Resolves `false` when nothing was committed, so a caller can fall back. */
  const commitAndAdvance = async (
    committed: CommittedSelection<Division>,
    { registrationTypeId, roleKey, consumeLink }: CommitOptions
  ): Promise<boolean> => {
    // `schedule_id` is what every downstream step keys off, and it is optional
    // on the type. Without it there is nothing valid to commit.
    const scheduleId = committed.division?.schedule_id
    if (!scheduleId) {
      return false
    }
    const teamId = teamIdentity(committed.team)
    // Read when committing, not when rendering: the auto-advance commits in
    // the same pass that may have just rewritten `?type=`.
    const {
      team: hasTeamDeepLink,
      division: hasDivisionDeepLink,
      role: hasRoleDeepLink,
    } = linkParamPresence(history.location.search)

    setIsSubmitting(true)
    try {
      // Keep EXISTING deep-link params honest — but never add one.
      //
      // The session carries the division, team and role, and every step restores
      // from it, so none of these params is needed to move a choice forward.
      // They matter only because a URL that names them is shareable and may be
      // copied onward: an id left pointing at something the reader has since
      // changed would send the next person to a division, team or role nobody
      // chose. A reader who arrived without them gets a clean URL and keeps it.
      //
      // Replace, not push: it annotates the step the reader is already on rather
      // than adding a history entry they would have to press Back through twice.
      if (hasTeamDeepLink || hasDivisionDeepLink || hasRoleDeepLink) {
        history.replace({
          search: withSelectionParams(
            history.location.search,
            {
              //  Null for a rep, whose commit carries no team: the teams tab
              //  is closed to them, so a lingering id would name a choice they
              //  cannot see.
              teamId: teamId ?? null,
              divisionScheduleId: scheduleId,
              role: roleKey,
            },
            {
              team: hasTeamDeepLink,
              division: hasDivisionDeepLink,
              role: hasRoleDeepLink,
            }
          ),
        })
      }

      // One PUT for everything this step asks. It used to be two — division,
      // then role — which was not atomic: a failure on the second left the
      // session on a division with no role.
      //
      // The role goes with it whether or not the reader is signed in: the types
      // come from `/types/registration`, which needs no account, so a logged-out
      // reader who answered the role question has an answer worth persisting.
      // Only when there is genuinely no role does this fall back to committing
      // the division alone.
      if (registrationTypeId) {
        await updateSessionStart({
          divisionId: scheduleId,
          registrationTypeId,
          // Undefined for a rep — see `committedSelection`.
          teamId,
          userId: userId ?? undefined,
        })
      } else {
        await updateSessionDivision(scheduleId, userId ?? undefined)
      }

      if (!isLogged) {
        // Pass one ends here: authenticate, then come back to this step.
        setStep(REGISTRATION_STEPS.SIGN_UP)
        return true
      }

      checkIfUserIsVerified()
        .then(() => {
          if (consumeLink) {
            // Replaced BEFORE the push below, so the start-step history entry
            // that Back returns to carries no link either.
            history.replace({
              search: withoutSelectionParams(history.location.search),
            })
          }
          // Hand the commit to the context the register step renders from — the
          // session alone left it holding an earlier pass's team and division.
          // Sign-up, login and confirm-email all route back to the start step,
          // and the retired select-role screen is redirected there by `Main`, so
          // this branch covers every arrival — whether Next or the `?team_id=`
          // auto-advance called it. Written right before navigating so it lands
          // in the same render as the route change, rather than re-scoping the
          // start step's grid while the reader waits.
          setContextSelectedTeam(committed.team)
          setDivision(committed.division)
          setStep(REGISTRATION_STEPS.TEAM_REGISTER)
        })
        .catch(() => setStep(REGISTRATION_STEPS.CONFIRM_YOUR_EMAIL))
      return true
    } catch (e: unknown) {
      const { status, stepMessage } = readSessionUpdateError(e)
      // A dead session cannot be recovered in place.
      if (status === 410) {
        deleteSession()
        window.location.reload()
        return false
      }
      message.error(
        getErrorMessage(e) ||
          stepMessage ||
          'Something Went Wrong, Please Try Again Later'
      )
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  return { commitAndAdvance, isSubmitting }
}
